import DocumentPage from './DocumentPage';

export const discussDocument = {
  slug: 'discuss',
  route: '/discuss',
  title: 'Discuss',
  category: 'Collaboration',
  format: 'DOCX',
  description:
    'The Discuss module supports internal communication with inbox management, starred conversations, channels, direct messages, mentions, notifications, and meetings.',
  metrics: [
    { label: 'Primary focus', value: 'Internal messaging' },
    { label: 'Core records', value: 'Inbox, channels, DMs' },
    { label: 'Real-time tools', value: 'Mentions and meetings' },
  ],
  workflow: [
    'Open the Discuss module from the main menu and review inbox activity, starred items, and recent conversations.',
    'Use channels for group communication and direct messages for one-to-one communication between staff members.',
    'Use mentions, notifications, and meetings to keep communication immediate and actionable.',
  ],
  sections: [
    {
      id: 'accessing-discuss',
      title: 'Accessing Discuss Module',
      body: [
        'The Discuss module is used for internal communication within the organization. It allows users to send messages, create channels, manage conversations, and collaborate in real time.',
        'In EduX, this module is used for communication between Admin, Teachers, and Staff.',
        'Go to Main Menu -> Discuss. The landing page opens in the Inbox by default, where incoming messages are displayed for review and response.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/discuss/image.png',
          alt: 'Discuss landing page showing the Inbox and left communication menu',
          caption:
            'Discuss landing page with Inbox, Starred, History, Channels, and Direct Messages visible from the left panel.',
        },
        {
          src: '/assets/doc-images/discuss/image3.png',
          alt: 'Discuss Starred screen for important saved messages',
          caption:
            'Starred messages help users quickly return to important conversations and notifications.',
        },
      ],
      bullets: [
        'Inbox contains all received messages and notifications.',
        'Starred stores messages marked as important for quick access.',
        'History helps users revisit earlier communication threads.',
        'Channels are used for shared team conversations.',
        'Direct Messages support private one-to-one communication.',
      ],
    },
    {
      id: 'channels',
      title: 'Channels',
      body: [
        'Channels are used for group communication. Multiple users can send messages, share updates, and collaborate within a channel.',
        'This is useful for broad communication such as organization announcements, team coordination, and follow-up discussions.',
      ],
      figure: {
        src: '/assets/doc-images/discuss/image5.png',
        alt: 'General channel in Discuss showing a welcome message and shared conversation area',
        caption:
          'A channel keeps communication visible to all members who need to collaborate on the same topic.',
      },
      steps: [
        'Open the Discuss module and locate the Channels section in the left panel.',
        'Click the + icon next to Channels to open the create window.',
        'Enter the channel name and add the required members.',
        'Save the channel and begin group communication in real time.',
      ],
      subsections: [
        {
          id: 'creating-a-channel',
          title: 'Creating a Channel',
          body: [
            'The create channel screen allows users to define a new communication space by entering the channel name and selecting members.',
            'This helps teams keep module-specific or department-specific conversations organized.',
          ],
          figure: {
            src: '/assets/doc-images/discuss/image6.png',
            alt: 'New Discuss channel created for announcements',
            caption:
              'A new channel can be created for focused communication such as announcements, team updates, or internal coordination.',
          },
        },
      ],
    },
    {
      id: 'direct-messages',
      title: 'Direct Messages',
      body: [
        'Direct messages allow private communication between two users. These are one-to-one conversations used for quick follow-up, coordination, and private discussion.',
        'Users can click on Administrator or any other listed user in the Direct Messages section to open the conversation thread.',
      ],
      figure: {
        src: '/assets/doc-images/discuss/image8.png',
        alt: 'OdooBot direct message conversation inside Discuss',
        caption:
          'Users can also open OdooBot to read suggestions, interact from the chat box, and receive instant replies.',
      },
      bullets: [
        'Click on Administrator to open a private conversation.',
        'Click on OdooBot to view system-guided messages and suggestions.',
        'Type the message in the chat box and send it instantly.',
        'Messages appear in real time inside the current conversation thread.',
      ],
    },
    {
      id: 'mentions-and-notifications',
      title: 'Mentions and Notifications',
      body: [
        'Mentions and notifications make conversations actionable by drawing attention to the right person at the right time.',
        'Users can type @Administrator or another user name to notify a specific person directly inside a message thread.',
      ],
      subsections: [
        {
          id: 'mentions',
          title: 'Mentions (@)',
          body: [
            'Using @mention, users can notify specific people in a conversation.',
            'This is helpful when a message needs action, review, or confirmation from a particular user.',
          ],
          figure: {
            src: '/assets/doc-images/discuss/image9.png',
            alt: 'Discuss message using @Administrator mention',
            caption:
              'Mentions highlight the targeted user inside the message so important requests are easier to notice.',
          },
        },
        {
          id: 'notifications',
          title: 'Notifications',
          body: [
            'The notification icon at the top right alerts users about new messages and activities.',
            'From the notification panel, users can quickly review recent updates across chats, channels, and direct messages.',
          ],
          figure: {
            src: '/assets/doc-images/discuss/imagea.png',
            alt: 'Discuss notification panel listing recent chats and channel activity',
            caption:
              'Notifications help users track recent activity without manually opening every conversation.',
          },
        },
      ],
    },
    {
      id: 'meeting-feature',
      title: 'Meeting Feature',
      body: [
        'The Discuss module also supports live communication through meetings. Users can click Start a Meeting to begin a call-oriented collaboration flow.',
        'This feature helps teams move from text communication to real-time discussion when immediate coordination is needed.',
      ],
      figure: {
        src: '/assets/doc-images/discuss/imageb.png',
        alt: 'Discuss meeting screen with live conference controls and invite panel',
        caption:
          'The meeting screen provides live conference controls and allows additional people to be invited to the group chat.',
      },
      steps: [
        'Open a direct message or discussion thread.',
        'Click Start a Meeting to begin the live conversation.',
        'Use the invite panel to add participants to the group chat or conference.',
        'Continue collaboration through voice or live discussion as needed.',
      ],
    },
    {
      id: 'use-of-discuss',
      title: 'Use of Discuss in EduX',
      body: [
        'The Discuss module in EduX is used for day-to-day internal communication and collaboration across departments.',
        'It improves visibility, speeds up internal coordination, and supports both individual and group communication workflows.',
      ],
      bullets: [
        'Communication between staff and admin',
        'Sharing important updates',
        'Group discussions',
        'Sending notifications',
      ],
    },
  ],
  related: ['calendar', 'to-dos', 'hr'],
};

export default function DiscussPage() {
  return <DocumentPage document={discussDocument} />;
}
